<template>
  <div class="whatsapp-settings-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="whatsapp-settings-container">
          <!-- Loading State -->
          <div v-if="isLoading" class="loading-container">
            <div class="loading-spinner">
              <svg class="spinner" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10" opacity="0.25"/>
                <path d="M12 2a10 10 0 0 1 10 10" opacity="0.75"/>
              </svg>
            </div>
            <p class="loading-text">Loading WhatsApp settings...</p>
          </div>

          <!-- Settings Content - Only show when loaded -->
          <div v-else class="settings-content">
            <!-- WhatsApp Embedded Signup Card - Only show when NOT connected -->
            <div v-if="!isConnected" class="settings-card embedded-signup-card">
              <div class="card-header">
                <h3 class="card-title">Quick Setup with Meta</h3>
                <p class="card-subtitle">Connect your WhatsApp Business Account in one click using Meta's secure authentication</p>
              </div>

              <div class="card-body">
                <div class="embedded-signup-content">
                  <div class="signup-buttons-container">
                    <button
                      @click="startEmbeddedSignup"
                      class="embedded-signup-button"
                      :disabled="isStartingEmbeddedSignup || isStartingCoexistenceSignup"
                    >
                      <svg v-if="!isStartingEmbeddedSignup" class="button-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      <span v-if="!isStartingEmbeddedSignup">Start with New Number</span>
                      <span v-else class="loading-spinner">
                        <span class="spinner"></span>
                        Connecting...
                      </span>
                    </button>

                    <button
                      v-if="false"
                      @click="startCoexistenceSignup"
                      class="coexistence-signup-button"
                      :disabled="true"
                    >
                      <svg v-if="!isStartingCoexistenceSignup" class="button-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      <span v-if="!isStartingCoexistenceSignup">Use with Existing Number</span>
                      <span v-else class="loading-text">
                        <svg class="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10" opacity="0.25"/>
                          <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"/>
                        </svg>
                        Connecting...
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- OTP Verification Card - Show when Account Review APPROVED & Phone Status PENDING -->
            <div
              v-if="accountReviewStatus.status === 'APPROVED' && phoneStatus.status === 'PENDING'"
              class="settings-card otp-verification-card"
            >
              <div class="otp-card-header">
                <div class="otp-icon-container">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </div>
                <div class="otp-header-content">
                  <h2 class="otp-title">One Last Step: Verify Your Phone Number</h2>
                  <p class="otp-subtitle">Enter the 6-digit verification code sent to your WhatsApp Business number</p>
                </div>
              </div>

              <div class="otp-card-body">
                <div class="otp-input-section">
                  <label for="otp-input" class="otp-label">Verification Code (PIN)</label>
                  <div class="otp-input-container">
                    <input
                      id="otp-input"
                      v-model="otpPin"
                      type="text"
                      maxlength="6"
                      placeholder="000000"
                      class="otp-input"
                      :disabled="isVerifyingOtp"
                      @input="handleOtpInput"
                      @keypress="onlyNumbers"
                      @keyup.enter="verifyOtp"
                    />
                    <span class="otp-counter">{{ otpPin.length }}/6</span>
                  </div>
                  <p class="otp-hint">Check your WhatsApp Business number for the verification code</p>
                </div>

                <div class="otp-actions">
                  <button
                    @click="verifyOtp"
                    class="otp-submit-btn"
                    :disabled="isVerifyingOtp || otpPin.length !== 6"
                  >
                    <svg v-if="!isVerifyingOtp" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                    <span v-if="!isVerifyingOtp">Verify & Activate</span>
                    <span v-else class="loading-text">
                      <svg class="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10" opacity="0.25"/>
                        <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"/>
                      </svg>
                      Verifying...
                    </span>
                  </button>
                </div>

                <div v-if="otpError" class="otp-error">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {{ otpError }}
                </div>

                <div v-if="otpSuccess" class="otp-success">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  {{ otpSuccess }}
                </div>
              </div>
            </div>

            <!-- Connected Account Info - Show for successful embedded signup -->
            <div v-if="isEmbeddedSignup && isConnected" class="settings-card connected-account-card">
              <div class="card-header">
                <h3 class="card-title">Connected Account Information</h3>
                <p class="card-subtitle">Your WhatsApp Business Account is connected via Meta Embedded Signup</p>
              </div>

              <div class="card-body">
                <div class="account-info-grid">
                  <div class="info-item">
                    <div class="info-label">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                        <line x1="12" y1="18" x2="12.01" y2="18"/>
                      </svg>
                      <span>Phone Number ID</span>
                    </div>
                    <div class="info-value">{{ settings.whatsappPhoneId || 'Not available' }}</div>
                  </div>

                  <div class="info-item">
                    <div class="info-label">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M20 7h-3a2 2 0 0 1-2-2V2"/>
                        <path d="M9 18a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h7l4 4v10a2 2 0 0 1-2 2Z"/>
                        <path d="M3 7.6v12.8A1.6 1.6 0 0 0 4.6 22h9.8"/>
                      </svg>
                      <span>Business Account ID</span>
                    </div>
                    <div class="info-value">{{ settings.whatsappBusinessAccountId || 'Not available' }}</div>
                  </div>

                  <div class="info-item">
                    <div class="info-label">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 2v20M2 12h20"/>
                      </svg>
                      <span>Setup Method</span>
                    </div>
                    <div class="info-value">
                      <span class="badge-embedded">Embedded Signup</span>
                    </div>
                  </div>

                  <div class="info-item">
                    <div class="info-label">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      </svg>
                      <span>Webhook Status</span>
                    </div>
                    <div class="info-value">
                      <span class="badge-webhook-active">Active & Secured</span>
                    </div>
                  </div>

                  <!-- Quali-D WhatsApp Connect Status -->
                  <div class="info-item">
                    <div class="info-label">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <span>Quali-D WhatsApp Connect</span>
                    </div>
                    <div class="info-value">
                      <span class="badge-qualid-active">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="margin-right: 4px;">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        Active
                      </span>
                    </div>
                  </div>

                  <!-- Phone Status -->
                  <div class="info-item">
                    <div class="info-label">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M12 6v6l4 2"/>
                      </svg>
                      <span>Phone Status</span>
                    </div>
                    <div class="info-value">
                      <span :class="['badge-phone-status', `status-${phoneStatus.status.toLowerCase()}`]">
                        {{ phoneStatus.status }}
                      </span>
                    </div>
                  </div>

                  <!-- Display Phone Number -->
                  <div class="info-item">
                    <div class="info-label">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                      </svg>
                      <span>Display Number</span>
                    </div>
                    <div class="info-value">{{ phoneStatus.displayNumber || 'Not available' }}</div>
                  </div>

                  <!-- Verified Name -->
                  <div class="info-item">
                    <div class="info-label">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                      <span>Verified Name</span>
                    </div>
                    <div class="info-value">{{ phoneStatus.verifiedName || 'Not verified' }}</div>
                  </div>

                  <!-- Quality Rating -->
                  <div class="info-item">
                    <div class="info-label">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                      </svg>
                      <span>Quality Rating</span>
                    </div>
                    <div class="info-value">
                      <span :class="['badge-quality-rating', `quality-${phoneStatus.qualityRating.toLowerCase()}`]">
                        {{ phoneStatus.qualityRating }}
                      </span>
                    </div>
                  </div>

                  <!-- Account Review Status -->
                  <div class="info-item" :class="{ 'info-item-warning': accountReviewStatus.status === 'PENDING' }">
                    <div class="info-label">
                      <svg v-if="accountReviewStatus.status === 'PENDING'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2" class="warning-icon-label">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/>
                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                      <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <span>Account Review</span>
                    </div>
                    <div class="info-value">
                      <span v-if="accountReviewStatus.isLoading" class="badge-loading">
                        <span class="spinner-small"></span>
                        Loading...
                      </span>
                      <span v-else-if="accountReviewStatus.status === 'PENDING'" class="badge-meta-review">
                        Under Meta Review
                      </span>
                      <span v-else :class="['badge-review-status', `review-${accountReviewStatus.status.toLowerCase()}`]">
                        {{ accountReviewStatus.status }}
                      </span>
                    </div>
                  </div>

                  <!-- Business Verification Status -->
                  <div class="info-item">
                    <div class="info-label">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <span>Business Verification</span>
                    </div>
                    <div class="info-value">
                      <span v-if="businessVerificationStatus.isLoading" class="badge-loading">
                        <span class="spinner-small"></span>
                        Loading...
                      </span>
                      <span v-else :class="['badge-verification-status', `verification-${businessVerificationStatus.status.toLowerCase()}`]">
                        {{ businessVerificationStatus.status }}
                      </span>
                    </div>
                  </div>

                  <!-- Messaging Limit -->
                  <div class="info-item">
                    <div class="info-label">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        <path d="M8 10h8"/>
                        <path d="M8 14h4"/>
                      </svg>
                      <span>Messaging Limit</span>
                    </div>
                    <div class="info-value">
                      <span :class="['badge-messaging-limit', `limit-${getMessagingLimitTier(messagingLimit)}`]">
                        {{ formatMessagingLimit(messagingLimit) }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Meta Review Status Card - PENDING -->
                <div v-if="accountReviewStatus.status === 'PENDING'" class="meta-review-card">
                  <div class="meta-review-header">
                    <div class="meta-review-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M12 6v6l4 2"/>
                      </svg>
                    </div>
                    <div class="meta-review-title-section">
                      <h4 class="meta-review-title">WhatsApp Status: Under Meta Review</h4>
                    </div>
                  </div>

                  <div class="meta-review-body">
                    <div class="meta-review-section">
                      <div class="meta-review-section-icon">⏳</div>
                      <div class="meta-review-section-content">
                        <h5 class="meta-review-section-title">What this means:</h5>
                        <ul class="meta-review-list">
                          <li>Your business is being reviewed by Meta</li>
                          <li>No action needed unless requested</li>
                        </ul>
                      </div>
                    </div>

                    <div class="meta-review-section">
                      <div class="meta-review-section-icon">⏱</div>
                      <div class="meta-review-section-content">
                        <h5 class="meta-review-section-title">Typical time:</h5>
                        <p class="meta-review-text">24 hours – 7 days</p>
                      </div>
                    </div>

                    <div class="meta-review-section">
                      <div class="meta-review-section-icon">🔍</div>
                      <div class="meta-review-section-content">
                        <h5 class="meta-review-section-title">Common reasons for delay:</h5>
                        <ul class="meta-review-list">
                          <li>New or unverified Business Manager</li>
                          <li>Business details mismatch</li>
                          <li>Previous WhatsApp blocks on this Meta account</li>
                        </ul>
                      </div>
                    </div>

                    <!-- What To Do Now Button -->
                    <div class="meta-review-action">
                      <a
                        href="https://quali-d.com/tutorials/whatsapp/waba-pending"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="what-to-do-btn"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10"/>
                          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                          <line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                        What To Do Now?
                      </a>
                    </div>
                  </div>
                </div>

                <!-- Warning for REJECTED account -->
                <div v-if="accountReviewStatus.status === 'REJECTED'" class="account-warning">
                  <div class="warning-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/>
                      <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                  </div>
                  <div class="warning-content">
                    <h4 class="warning-title">Account Review Rejected</h4>
                    <p class="warning-message">
                      Your WhatsApp Business Account has been rejected. You may not be able to send messages until this is resolved.
                    </p>
                    <a
                      :href="`https://business.facebook.com/wa/manage/account-review/?business_id=${settings.whatsappBusinessAccountId}`"
                      target="_blank"
                      class="warning-action-link"
                    >
                      Review Account Status on Facebook
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                        <polyline points="15 3 21 3 21 9"/>
                        <line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                    </a>
                  </div>
                </div>

                <!-- Info for business verification - Only show if verification is pending or required -->
                <div v-if="businessVerificationStatus.status === 'PENDING' || businessVerificationStatus.status === 'REQUIRED' || businessVerificationStatus.status === 'NOT_VERIFIED'" class="verification-info">
                  <div class="info-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="16" x2="12" y2="12"/>
                      <line x1="12" y1="8" x2="12.01" y2="8"/>
                    </svg>
                  </div>
                  <div class="info-content">
                    <h4 class="info-title">Business Verification Required</h4>
                    <p class="info-message">
                      Business verification is required to increase your messaging limits. You can still use WhatsApp, but with lower limits.
                    </p>
                    <a
                      :href="`https://business.facebook.com/settings/info?business_id=${settings.whatsappBusinessAccountId}`"
                      target="_blank"
                      class="info-action-link"
                    >
                      Complete Business Verification
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                        <polyline points="15 3 21 3 21 9"/>
                        <line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                    </a>
                  </div>
                </div>

                <!-- Messaging Limit Info - Only show for users below TIER_2K -->
                <div v-if="messagingLimit === 'TIER_250' || messagingLimit === 'TIER_1K'" class="messaging-limit-info">
                  <div class="limit-info-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 16v-4"/>
                      <path d="M12 8h.01"/>
                    </svg>
                  </div>
                  <div class="limit-info-content">
                    <h4 class="limit-info-title">{{ getMessagingLimitTitle(messagingLimit) }}</h4>
                    <p class="limit-info-message">{{ getMessagingLimitMessage(messagingLimit) }}</p>
                    <div class="scaling-paths">
                      <h5>Ways to increase your limit to 2,000:</h5>
                      <ul>
                        <li><strong>Verify your business</strong> - Get instant approval to TIER_2K</li>
                        <li><strong>Send quality messages</strong> - Send 2,000 delivered messages to unique users in 30 days</li>
                      </ul>
                      <h5 style="margin-top: 12px;">Automatic scaling after TIER_2K:</h5>
                      <p style="font-size: 13px; margin: 6px 0;">Send high-quality messages and use at least 50% of your current limit in 7 days to automatically scale to the next tier within 6 hours.</p>
                    </div>
                    <a
                      :href="`https://business.facebook.com/settings/info?business_id=${settings.whatsappBusinessAccountId}`"
                      target="_blank"
                      class="limit-action-link"
                    >
                      Increase Your Messaging Limit
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                        <polyline points="15 3 21 3 21 9"/>
                        <line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                    </a>
                  </div>
                </div>

                <!-- Refresh Status Button -->
                <div class="phone-status-refresh">
                  <button @click="refreshPhoneStatus" class="refresh-status-button" :disabled="isCheckingStatus">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :class="{ 'spinning': isCheckingStatus }">
                      <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                    </svg>
                    <span v-if="!isCheckingStatus">Refresh Phone Status</span>
                    <span v-else>Checking with Meta...</span>
                  </button>
                  <span v-if="phoneStatus.lastCheck" class="last-check-time">
                    Last checked: {{ new Date(phoneStatus.lastCheck).toLocaleString() }}
                  </span>
                </div>

                <div class="account-actions">
                  <button @click="startEmbeddedSignup" class="reconnect-button">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                    </svg>
                    Reconnect Account
                  </button>
                  <button @click="disconnectAccount" class="disconnect-button">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                      <polyline points="16 17 21 12 16 7"/>
                      <line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                    Disconnect Account
                  </button>
                </div>
              </div>
            </div>

            <!-- WhatsApp API Configuration Card - Manual setup (only show if not connected via embedded signup) -->
            <div v-if="!(isEmbeddedSignup && isConnected)" class="settings-card">
              <div class="card-header">
                <h3 class="card-title">Manual WhatsApp API Configuration</h3>
                <p class="card-subtitle">{{ isEmbeddedSignup ? 'Complete your setup manually' : 'Manage your WhatsApp Business API credentials manually' }}</p>
              </div>

              <div class="card-body">
                <!-- Embedded Signup Recommendation Banner -->
                <div v-if="!isEmbeddedSignup" class="recommendation-banner">
                  <div class="banner-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M12 2v20M2 12h20"/>
                      <circle cx="12" cy="12" r="10"/>
                    </svg>
                  </div>
                  <div class="banner-content">
                    <div class="banner-title">💡 {{ isConnected ? 'Upgrade to Embedded Signup' : 'Recommended: Use Embedded Signup' }}</div>
                    <p class="banner-message">
                      {{ isConnected ? 'Migrate to a better setup method!' : 'Save time and avoid manual configuration errors!' }} Our <strong>Embedded Signup</strong> feature automatically connects your WhatsApp Business Account {{ isConnected ? 'with better security and management' : 'in under 2 minutes with zero manual data entry' }}.
                    </p>
                    <div class="banner-benefits">
                      <div class="benefit-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"/>
                        </svg>
                        <span>No manual token copying</span>
                      </div>
                      <div class="benefit-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"/>
                        </svg>
                        <span>Instant webhook setup</span>
                      </div>
                      <div class="benefit-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"/>
                        </svg>
                        <span>Secure OAuth 2.0 authentication</span>
                      </div>
                      <div class="benefit-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"/>
                        </svg>
                        <span>5x faster than manual setup</span>
                      </div>
                    </div>
                    <button
                      @click="startEmbeddedSignup"
                      class="banner-cta-button"
                      :disabled="isStartingEmbeddedSignup"
                    >
                      <svg v-if="!isStartingEmbeddedSignup" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M10,17L15,12L10,7V17Z"/>
                      </svg>
                      <span v-if="!isStartingEmbeddedSignup">Try Embedded Signup Instead</span>
                      <span v-else class="loading-spinner-inline">
                        <span class="spinner-small"></span>
                        Starting...
                      </span>
                    </button>
                  </div>
                </div>

                <!-- Missing WABA ID Warning for incomplete embedded signup -->
                <div v-if="isEmbeddedSignup && !settings.whatsappBusinessAccountId" class="warning-banner">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
                    <path d="M12 9v4"/>
                    <path d="M12 17h.01"/>
                  </svg>
                  <div>
                    <strong>Missing WhatsApp Business Account ID</strong>
                    <p>Your embedded signup didn't complete. Please disconnect and reconnect, or add the WABA ID manually below.</p>
                  </div>
                </div>

                <form @submit.prevent="updateSettings" class="settings-form">
                  <div class="form-group">
                    <label for="whatsappToken" class="form-label">WhatsApp Access Token</label>
                    <div class="input-wrapper">
                      <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <circle cx="12" cy="7" r="4"/>
                        <path d="m8 11 0-4a4 4 0 0 1 8 0v4"/>
                      </svg>
                      <input
                        id="whatsappToken"
                        v-model="settings.whatsappToken"
                        :type="showToken ? 'text' : 'password'"
                        placeholder="Enter your WhatsApp access token"
                        class="form-input"
                        :class="{ 'error': errors.whatsappToken, 'readonly-field': isEmbeddedSignup }"
                        autocomplete="off"
                        autocorrect="off"
                        autocapitalize="off"
                        spellcheck="false"
                        data-lpignore="true"
                        data-form-type="other"
                        :readonly="isEmbeddedSignup"
                        :required="!isEmbeddedSignup"
                      />
                      <button
                        type="button"
                        @click="toggleTokenVisibility"
                        class="toggle-password"
                      >
                        <svg v-if="!showToken" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="m9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                          <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68"/>
                          <path d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61"/>
                          <line x1="2" y1="2" x2="22" y2="22"/>
                        </svg>
                      </button>
                    </div>
                    <span v-if="errors.whatsappToken" class="error-message">{{ errors.whatsappToken }}</span>
                  </div>

                  <div class="form-group">
                    <label for="whatsappPhoneId" class="form-label">WhatsApp Phone Number ID</label>
                    <div class="input-wrapper">
                      <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                        <line x1="12" y1="18" x2="12.01" y2="18"/>
                      </svg>
                      <input
                        id="whatsappPhoneId"
                        v-model="settings.whatsappPhoneId"
                        type="text"
                        placeholder="Enter your WhatsApp phone number ID"
                        class="form-input"
                        :class="{ 'error': errors.whatsappPhoneId, 'readonly-field': isEmbeddedSignup }"
                        autocomplete="off"
                        autocorrect="off"
                        autocapitalize="off"
                        spellcheck="false"
                        data-lpignore="true"
                        data-form-type="other"
                        :readonly="isEmbeddedSignup"
                        :required="!isEmbeddedSignup"
                      />
                    </div>
                    <span v-if="errors.whatsappPhoneId" class="error-message">{{ errors.whatsappPhoneId }}</span>
                  </div>

                  <div class="form-group">
                    <label for="whatsappBusinessAccountId" class="form-label">WhatsApp Business Account ID</label>
                    <div class="input-wrapper">
                      <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M20 7h-3a2 2 0 0 1-2-2V2"/>
                        <path d="M9 18a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h7l4 4v10a2 2 0 0 1-2 2Z"/>
                        <path d="M3 7.6v12.8A1.6 1.6 0 0 0 4.6 22h9.8"/>
                      </svg>
                      <input
                        id="whatsappBusinessAccountId"
                        v-model="settings.whatsappBusinessAccountId"
                        type="text"
                        name="waba_id"
                        placeholder="Enter your WhatsApp business account ID"
                        class="form-input"
                        :class="{ 'error': errors.whatsappBusinessAccountId }"
                        autocomplete="off"
                        autocorrect="off"
                        autocapitalize="off"
                        spellcheck="false"
                        data-lpignore="true"
                        data-form-type="other"
                        data-1p-ignore="true"
                        readonly
                        onfocus="if(!this.classList.contains('readonly-field')) this.removeAttribute('readonly')"
                        :required="!isEmbeddedSignup"
                      />
                    </div>
                    <span v-if="errors.whatsappBusinessAccountId" class="error-message">{{ errors.whatsappBusinessAccountId }}</span>
                  </div>

                  <div class="form-group">
                    <label for="webhookSecretKey" class="form-label">Webhook Secret Key</label>
                    <div class="input-wrapper">
                      <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      </svg>
                      <input
                        id="webhookSecretKey"
                        v-model="settings.webhookSecretKey"
                        :type="showWebhookKey ? 'text' : 'password'"
                        placeholder="Enter your webhook secret key"
                        class="form-input"
                        :class="{ 'error': errors.webhookSecretKey, 'readonly-field': isEmbeddedSignup }"
                        autocomplete="off"
                        autocorrect="off"
                        autocapitalize="off"
                        spellcheck="false"
                        data-lpignore="true"
                        data-form-type="other"
                        :readonly="isEmbeddedSignup"
                        :required="!isEmbeddedSignup"
                      />
                      <button
                        type="button"
                        @click="toggleWebhookKeyVisibility"
                        class="toggle-password"
                      >
                        <svg v-if="!showWebhookKey" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="m9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                          <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68"/>
                          <path d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61"/>
                          <line x1="2" y1="2" x2="22" y2="22"/>
                        </svg>
                      </button>
                    </div>
                    <span v-if="errors.webhookSecretKey" class="error-message">{{ errors.webhookSecretKey }}</span>
                  </div>

                  <button
                    type="submit"
                    class="update-button"
                    :disabled="isUpdating"
                    :class="{ 'loading': isUpdating }"
                  >
                    <span v-if="!isUpdating">Update WhatsApp API Settings</span>
                    <span v-else class="loading-spinner">
                      <span class="spinner"></span>
                      Updating...
                    </span>
                  </button>
                </form>
              </div>
            </div>

            <!-- Webhook Configuration Card - Manual setup (only show if not connected via embedded signup) -->
            <div v-if="!(isEmbeddedSignup && isConnected)" class="settings-card">
              <div class="card-header">
                <h3 class="card-title">Webhook Configuration</h3>
                <p class="card-subtitle">Copy webhook URL and verification token for WhatsApp setup</p>
              </div>

              <div class="card-body">
                <div class="webhook-actions">
                  <button
                    @click="copyWebhookUrl"
                    class="action-button webhook-button"
                    :disabled="isLoadingWebhook"
                  >
                    <svg class="button-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="m5 15-4-4 4-4"/>
                      <path d="m11 19 4-4-4-4"/>
                    </svg>
                    <span v-if="!isLoadingWebhook">Copy Webhook URL</span>
                    <span v-else>Loading...</span>
                  </button>

                  <button
                    @click="copyVerifyToken"
                    class="action-button verify-button"
                  >
                    <svg class="button-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="m5 15-4-4 4-4"/>
                    </svg>
                    Copy Verify Token
                  </button>
                </div>
              </div>
            </div>

            <!-- Success/Error Messages -->
            <div v-if="successMessage" class="success-message">
              {{ successMessage }}
            </div>

            <div v-if="errorMessage" class="error-message-card">
              {{ errorMessage }}
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { AuthService } from '@/services/auth'
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'

export default {
  name: 'WhatsAppSettingsView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      settings: {
        whatsappToken: '',
        whatsappPhoneId: '',
        whatsappBusinessAccountId: '',
        webhookSecretKey: '', // Only used for manual setup, never loaded from API
        setupMethod: 'manual'
      },
      phoneStatus: {
        status: 'UNKNOWN',
        nameStatus: 'UNKNOWN',
        qualityRating: 'UNKNOWN',
        codeVerificationStatus: 'UNKNOWN',
        verifiedName: null,
        displayNumber: null,
        lastCheck: null
      },
      accountReviewStatus: {
        status: 'UNKNOWN', // PENDING, APPROVED, REJECTED
        isLoading: false
      },
      businessVerificationStatus: {
        status: 'UNKNOWN',
        isLoading: false
      },
      messagingLimit: 'UNKNOWN', // TIER_250, TIER_2K, TIER_10K, TIER_100K, TIER_UNLIMITED
      showToken: false,
      showWebhookKey: false,
      isLoading: true,
      isUpdating: false,
      isLoadingWebhook: false,
      isCheckingStatus: false,
      isStartingEmbeddedSignup: false,
      isStartingCoexistenceSignup: false,
      fbAppId: null,
      fbSdkInitialized: false,
      errors: {},
      successMessage: '',
      errorMessage: '',
      coexistenceEnabled: false,
      coexistencePin: '',
      enablingCoexistence: false,
      // OTP Verification
      otpPin: '',
      isVerifyingOtp: false,
      otpError: '',
      otpSuccess: ''
    }
  },
  computed: {
    isConnected () {
      // Connected means: has token, phone ID, AND business account ID
      return !!(this.settings.whatsappToken &&
                this.settings.whatsappPhoneId &&
                this.settings.whatsappBusinessAccountId)
    },
    isEmbeddedSignup () {
      return this.settings.setupMethod === 'embedded_signup'
    }
  },
  async mounted () {
    await this.loadSettings()
    // Check if returning from embedded signup
    this.checkEmbeddedSignupCallback()
    // Initialize FB SDK
    this.initializeFacebookSDK()
    // Listen for messages from popup window
    window.addEventListener('message', this.handlePopupMessage)
    // Check coexistence status if connected via embedded signup
    if (this.isConnected && this.isEmbeddedSignup) {
      this.checkCoexistenceStatus()
      // Auto-refresh phone status on page load
      await this.refreshPhoneStatus()
    }
  },
  beforeUnmount () {
    // Clean up event listener
    window.removeEventListener('message', this.handlePopupMessage)
  },
  methods: {
    async loadSettings () {
      try {
        this.isLoading = true
        this.errorMessage = ''

        const token = AuthService.getAuthToken()
        if (!token) {
          throw new Error('Authentication token not found')
        }

        const response = await fetch('https://api.quali-d.com/whatsapp-credentials', {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (!response.ok) {
          throw new Error('Failed to load WhatsApp settings')
        }

        const data = await response.json()

        this.settings = {
          whatsappToken: data.credentials.whatsapp_token || '',
          whatsappPhoneId: data.credentials.whatsapp_phone_id || '',
          whatsappBusinessAccountId: data.credentials.whatsapp_business_account_id || '',
          setupMethod: data.credentials.setup_method || 'manual'
        }

        // Load cached phone status from database
        this.phoneStatus = {
          status: data.credentials.phone_status || 'UNKNOWN',
          nameStatus: data.credentials.phone_name_status || 'UNKNOWN',
          qualityRating: data.credentials.phone_quality_rating || 'UNKNOWN',
          codeVerificationStatus: data.credentials.phone_code_verification_status || 'UNKNOWN',
          verifiedName: data.credentials.phone_verified_name || null,
          displayNumber: data.credentials.phone_display_number || null,
          lastCheck: data.credentials.phone_status_last_check || null
        }

        // Get account statuses from backend API (already fetched by backend)
        this.accountReviewStatus.status = data.credentials.account_review_status || 'UNKNOWN'
        this.businessVerificationStatus.status = data.credentials.business_verification_status || 'UNKNOWN'
        this.messagingLimit = data.credentials.messaging_limit || 'UNKNOWN'
      } catch (error) {
        console.error('Error loading settings:', error)
        this.errorMessage = error.message || 'Failed to load WhatsApp settings'
      } finally {
        this.isLoading = false
      }
    },

    toggleTokenVisibility () {
      this.showToken = !this.showToken
    },

    toggleWebhookKeyVisibility () {
      this.showWebhookKey = !this.showWebhookKey
    },

    validateForm () {
      this.errors = {}
      let isValid = true

      if (!this.settings.whatsappToken.trim()) {
        this.errors.whatsappToken = 'WhatsApp access token is required'
        isValid = false
      }

      if (!this.settings.whatsappPhoneId.trim()) {
        this.errors.whatsappPhoneId = 'WhatsApp phone number ID is required'
        isValid = false
      }

      if (!this.settings.whatsappBusinessAccountId.trim()) {
        this.errors.whatsappBusinessAccountId = 'WhatsApp business account ID is required'
        isValid = false
      }

      if (!this.settings.webhookSecretKey.trim()) {
        this.errors.webhookSecretKey = 'Webhook secret key is required'
        isValid = false
      }

      return isValid
    },

    async updateSettings () {
      this.successMessage = ''
      this.errorMessage = ''

      if (!this.validateForm()) {
        return
      }

      this.isUpdating = true

      try {
        const token = AuthService.getAuthToken()
        if (!token) {
          throw new Error('Authentication token not found')
        }

        const response = await fetch('https://api.quali-d.com/whatsapp-credentials', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            whatsapp_token: this.settings.whatsappToken,
            whatsapp_phone_id: this.settings.whatsappPhoneId,
            whatsapp_business_account_id: this.settings.whatsappBusinessAccountId,
            webhook_secret_key: this.settings.webhookSecretKey
          })
        })

        if (!response.ok) {
          throw new Error('Failed to update WhatsApp settings')
        }

        const data = await response.json()

        if (data.success !== true) {
          throw new Error(data.message || 'Failed to update settings')
        }

        this.successMessage = 'WhatsApp API settings updated successfully!'

        setTimeout(() => {
          this.successMessage = ''
        }, 5000)
      } catch (error) {
        console.error('Error updating settings:', error)
        this.errorMessage = error.message || 'Failed to update WhatsApp settings'
      } finally {
        this.isUpdating = false
      }
    },

    async copyWebhookUrl () {
      try {
        this.isLoadingWebhook = true
        this.errorMessage = ''

        const token = AuthService.getAuthToken()
        if (!token) {
          throw new Error('Authentication token not found')
        }

        const response = await fetch('https://api.quali-d.com/get-whatsapp-inst-wh', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error('Failed to get webhook URL')
        }

        const data = await response.json()

        if (!data.success || !data.webhook_url) {
          throw new Error(data.message || 'Invalid webhook response')
        }

        await navigator.clipboard.writeText(data.webhook_url)
        this.successMessage = 'Webhook URL copied to clipboard successfully!'

        setTimeout(() => {
          this.successMessage = ''
        }, 3000)
      } catch (error) {
        console.error('Error copying webhook URL:', error)
        this.errorMessage = error.message || 'Failed to copy webhook URL'
      } finally {
        this.isLoadingWebhook = false
      }
    },

    async copyVerifyToken () {
      try {
        const verifyToken = 'secret@2024'
        await navigator.clipboard.writeText(verifyToken)
        this.successMessage = 'Verify token copied to clipboard successfully!'

        setTimeout(() => {
          this.successMessage = ''
        }, 3000)
      } catch (error) {
        console.error('Error copying verify token:', error)
        this.errorMessage = 'Failed to copy verify token to clipboard'
      }
    },

    // Embedded Signup Methods
    initializeFacebookSDK () {
      // Wait for FB SDK to load, then initialize with app ID from backend
      window.fbAsyncInit = () => {
        const appId = this.fbAppId || '164859516652717' // Fallback to your app ID
        // eslint-disable-next-line no-undef
        FB.init({
          appId: appId,
          autoLogAppEvents: true,
          xfbml: true,
          version: 'v21.0'
        })
        this.fbSdkInitialized = true
        console.log('Facebook SDK initialized with App ID:', appId)
      }

      // If FB SDK already loaded, initialize immediately
      // eslint-disable-next-line no-undef
      if (typeof FB !== 'undefined') {
        window.fbAsyncInit()
      }
    },

    async startEmbeddedSignup () {
      try {
        this.isStartingEmbeddedSignup = true
        this.errorMessage = ''

        const token = AuthService.getAuthToken()
        if (!token) {
          throw new Error('Authentication token not found')
        }

        // Get the signup URL from backend
        const response = await fetch('https://api.quali-d.com/whatsapp-embedded-signup/url', {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (!response.ok) {
          throw new Error('Failed to get signup URL')
        }

        const data = await response.json()

        if (!data.success || !data.signup_url) {
          throw new Error(data.message || 'Invalid signup URL response')
        }

        // Open OAuth URL in a popup window
        const width = 600
        const height = 700
        const left = (window.screen.width - width) / 2
        const top = (window.screen.height - height) / 2

        const popup = window.open(
          data.signup_url,
          'WhatsApp Embedded Signup',
          `width=${width},height=${height},left=${left},top=${top},toolbar=0,menubar=0,location=0,scrollbars=1`
        )

        if (!popup) {
          this.errorMessage = 'Popup was blocked. Please allow popups for this site.'
          this.isStartingEmbeddedSignup = false
          return
        }

        // Monitor popup for completion
        const popupMonitor = setInterval(() => {
          if (popup.closed) {
            clearInterval(popupMonitor)
            // Check if callback was successful after popup closes
            setTimeout(() => {
              // Reload settings to see if credentials were saved
              this.loadSettings()
              // If we're still in loading state and no success message, user probably cancelled
              if (this.isStartingEmbeddedSignup && !this.successMessage) {
                this.isStartingEmbeddedSignup = false
              }
            }, 1000)
          }
        }, 500)
      } catch (error) {
        console.error('Error starting embedded signup:', error)
        this.errorMessage = error.message || 'Failed to start WhatsApp signup'
        this.isStartingEmbeddedSignup = false
      }
    },

    async startCoexistenceSignup () {
      try {
        this.isStartingCoexistenceSignup = true
        this.errorMessage = ''

        const token = AuthService.getAuthToken()
        if (!token) {
          throw new Error('Authentication token not found')
        }

        // Get the coexistence signup URL from backend
        const response = await fetch('https://api.quali-d.com/whatsapp-embedded-signup/coexistence-url', {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (!response.ok) {
          throw new Error('Failed to get coexistence signup URL')
        }

        const data = await response.json()

        if (!data.success || !data.signup_url) {
          throw new Error(data.message || 'Invalid signup URL response')
        }

        // Open OAuth URL in a popup window
        const width = 600
        const height = 700
        const left = (window.screen.width - width) / 2
        const top = (window.screen.height - height) / 2

        const popup = window.open(
          data.signup_url,
          'WhatsApp Coexistence Signup',
          `width=${width},height=${height},left=${left},top=${top},toolbar=0,menubar=0,location=0,scrollbars=1`
        )

        if (!popup) {
          this.errorMessage = 'Popup was blocked. Please allow popups for this site.'
          this.isStartingCoexistenceSignup = false
          return
        }

        // Monitor popup for completion
        const popupMonitor = setInterval(() => {
          if (popup.closed) {
            clearInterval(popupMonitor)
            // Check if callback was successful after popup closes
            setTimeout(() => {
              // Reload settings to see if credentials were saved
              this.loadSettings()
              // If we're still in loading state and no success message, user probably cancelled
              if (this.isStartingCoexistenceSignup && !this.successMessage) {
                this.isStartingCoexistenceSignup = false
              }
            }, 1000)
          }
        }, 500)
      } catch (error) {
        console.error('Error starting coexistence signup:', error)
        this.errorMessage = error.message || 'Failed to start WhatsApp coexistence signup'
        this.isStartingCoexistenceSignup = false
      }
    },

    checkEmbeddedSignupCallback () {
      // Check URL parameters for embedded signup callback
      const urlParams = new URLSearchParams(window.location.search)
      const embeddedSignupStatus = urlParams.get('embedded_signup')
      const state = urlParams.get('state')

      if (embeddedSignupStatus === 'complete') {
        // Automatic completion - everything was saved in backend
        this.isStartingEmbeddedSignup = false
        this.successMessage = 'WhatsApp Business Account connected successfully! All credentials have been saved.'

        // Reload settings to show the new credentials
        setTimeout(() => {
          this.loadSettings()
          this.successMessage = ''
        }, 3000)

        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname)
      } else if (embeddedSignupStatus === 'success' && state) {
        // Fallback: Manual completion required
        this.isStartingEmbeddedSignup = false
        this.successMessage = 'WhatsApp account connected successfully! Please complete the setup below.'

        // Prompt user to complete the setup
        this.promptCompleteEmbeddedSignup()

        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname)
      }
    },

    handlePopupMessage (event) {
      // Only accept messages from our own origin
      if (event.origin !== window.location.origin) {
        return
      }

      // Check if it's a whatsapp embedded signup message
      if (event.data && event.data.type === 'whatsapp-embedded-signup') {
        this.isStartingEmbeddedSignup = false

        if (event.data.status === 'complete') {
          this.successMessage = 'WhatsApp Business Account connected successfully!'
          // Reload settings to show new credentials
          setTimeout(() => {
            this.loadSettings()
            this.successMessage = ''
          }, 2000)
        } else if (event.data.status === 'error') {
          this.errorMessage = event.data.error || 'Failed to connect WhatsApp account'
        }
      }
    },

    async disconnectAccount () {
      if (!confirm('Are you sure you want to disconnect your WhatsApp Business Account? You will need to reconnect to use WhatsApp features.')) {
        return
      }

      try {
        const token = AuthService.getAuthToken()
        if (!token) {
          throw new Error('Authentication token not found')
        }

        // Call backend to delete credentials
        const response = await fetch('https://api.quali-d.com/whatsapp-credentials', {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (!response.ok) {
          throw new Error('Failed to disconnect account')
        }

        this.successMessage = 'WhatsApp account disconnected successfully!'

        // Clear local settings
        this.settings = {
          whatsappToken: '',
          whatsappPhoneId: '',
          whatsappBusinessAccountId: '',
          webhookSecretKey: '',
          setupMethod: 'manual'
        }

        setTimeout(() => {
          this.successMessage = ''
        }, 3000)
      } catch (error) {
        console.error('Error disconnecting account:', error)
        this.errorMessage = error.message || 'Failed to disconnect WhatsApp account'
      }
    },

    async promptCompleteEmbeddedSignup () {
      try {
        const token = AuthService.getAuthToken()
        if (!token) {
          throw new Error('Authentication token not found')
        }

        // In a real implementation, you would show a modal here
        // asking the user to select their phone number and WABA ID
        // For now, we'll use prompt dialogs (you should replace this with a proper modal)

        const phoneNumberId = prompt('Please enter your WhatsApp Phone Number ID from Meta:')
        const wabaId = prompt('Please enter your WhatsApp Business Account ID (WABA):')

        if (!phoneNumberId || !wabaId) {
          this.errorMessage = 'Phone Number ID and WABA ID are required to complete setup'
          return
        }

        // Complete the embedded signup
        const response = await fetch('https://api.quali-d.com/whatsapp-embedded-signup/complete', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            phone_number_id: phoneNumberId,
            waba_id: wabaId
          })
        })

        if (!response.ok) {
          throw new Error('Failed to complete embedded signup')
        }

        const data = await response.json()

        if (!data.success) {
          throw new Error(data.message || 'Failed to complete setup')
        }

        this.successMessage = 'WhatsApp Business Account setup completed successfully!'

        // Reload settings to show the new credentials
        setTimeout(() => {
          this.loadSettings()
        }, 2000)
      } catch (error) {
        console.error('Error completing embedded signup:', error)
        this.errorMessage = error.message || 'Failed to complete WhatsApp setup'
      }
    },

    async checkCoexistenceStatus () {
      try {
        const token = AuthService.getAuthToken()
        const response = await fetch('https://api.quali-d.com/whatsapp/coexistence/status', {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        const data = await response.json()
        if (data.success) {
          this.coexistenceEnabled = data.coexistence_enabled
        }
      } catch (error) {
        console.error('Error checking coexistence status:', error)
      }
    },

    validatePin () {
      // Only allow digits
      this.coexistencePin = this.coexistencePin.replace(/\D/g, '')
    },

    async enableCoexistence () {
      if (this.coexistencePin.length !== 6) {
        alert('Please enter a 6-digit PIN')
        return
      }

      this.enablingCoexistence = true

      try {
        const token = AuthService.getAuthToken()
        const response = await fetch('https://api.quali-d.com/whatsapp/coexistence/enable', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            pin: this.coexistencePin
          })
        })

        const data = await response.json()

        if (data.success) {
          this.successMessage = 'Coexistence mode enabled successfully! You can now use both WhatsApp Business App and our Cloud API.'
          this.coexistenceEnabled = true
          this.coexistencePin = ''
          setTimeout(() => {
            this.successMessage = ''
          }, 5000)
        } else {
          this.errorMessage = `Failed to enable coexistence mode: ${data.message}`
          setTimeout(() => {
            this.errorMessage = ''
          }, 5000)
        }
      } catch (error) {
        console.error('Error enabling coexistence:', error)
        this.errorMessage = 'Failed to enable coexistence mode. Please try again.'
        setTimeout(() => {
          this.errorMessage = ''
        }, 5000)
      } finally {
        this.enablingCoexistence = false
      }
    },

    // OTP Verification Methods
    handleOtpInput (event) {
      // Only allow numbers
      this.otpPin = this.otpPin.replace(/\D/g, '')
      this.otpError = ''
      this.otpSuccess = ''
    },

    onlyNumbers (event) {
      // Allow only numbers
      const char = String.fromCharCode(event.keyCode)
      if (!/[0-9]/.test(char)) {
        event.preventDefault()
      }
    },

    async verifyOtp () {
      if (this.otpPin.length !== 6) {
        this.otpError = 'Please enter a 6-digit verification code'
        return
      }

      try {
        this.isVerifyingOtp = true
        this.otpError = ''
        this.otpSuccess = ''

        const token = AuthService.getAuthToken()
        if (!token) {
          throw new Error('Authentication token not found')
        }

        const response = await fetch('https://api.quali-d.com/whatsapp/verify-phone-number', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            phone_number_id: this.settings.whatsappPhoneId,
            pin: this.otpPin,
            whatsapp_token: this.settings.whatsappToken
          })
        })

        const data = await response.json()

        if (data.success) {
          this.otpSuccess = data.message || 'Phone number verified successfully!'
          this.otpPin = ''

          // Refresh phone status after successful verification
          setTimeout(async () => {
            await this.refreshPhoneStatus()
          }, 2000)
        } else {
          this.otpError = data.message || 'Verification failed. Please check the code and try again.'
        }
      } catch (error) {
        console.error('OTP verification error:', error)
        this.otpError = 'An error occurred during verification. Please try again.'
      } finally {
        this.isVerifyingOtp = false
      }
    },

    async refreshPhoneStatus () {
      try {
        this.isCheckingStatus = true

        const token = AuthService.getAuthToken()
        if (!token) {
          throw new Error('Authentication token not found')
        }

        const response = await fetch('https://api.quali-d.com/whatsapp-phone-status', {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (!response.ok) {
          throw new Error('Failed to refresh phone status')
        }

        const data = await response.json()

        if (data.success && data.phone_status) {
          this.phoneStatus = {
            status: data.phone_status.status || 'UNKNOWN',
            nameStatus: data.phone_status.name_status || 'UNKNOWN',
            qualityRating: data.phone_status.quality_rating || 'UNKNOWN',
            codeVerificationStatus: data.phone_status.code_verification_status || 'UNKNOWN',
            verifiedName: data.phone_status.verified_name || null,
            displayNumber: data.phone_status.display_phone_number || null,
            lastCheck: new Date().toISOString()
          }

          console.log('✅ Phone status refreshed from Meta:', this.phoneStatus)
        }
      } catch (error) {
        console.error('❌ Error refreshing phone status:', error)
      } finally {
        this.isCheckingStatus = false
      }
    },

    // Messaging Limit Helper Methods
    formatMessagingLimit (limit) {
      const limitMap = {
        TIER_250: '250 messages',
        TIER_2K: '2,000 messages',
        TIER_10K: '10,000 messages',
        TIER_100K: '100,000 messages',
        TIER_UNLIMITED: 'Unlimited',
        UNKNOWN: 'Unknown'
      }
      return limitMap[limit] || limit
    },

    getMessagingLimitTier (limit) {
      if (limit === 'TIER_250') return '250'
      if (limit === 'TIER_2K') return '2k'
      if (limit === 'TIER_10K') return '10k'
      if (limit === 'TIER_100K') return '100k'
      if (limit === 'TIER_UNLIMITED') return 'unlimited'
      return 'unknown'
    },

    getMessagingLimitTitle (limit) {
      if (limit === 'TIER_250') return 'Starter Messaging Limit (250)'
      if (limit === 'TIER_2K') return 'Growing Business (2,000 messages)'
      if (limit === 'TIER_10K') return 'Established Business (10,000 messages)'
      if (limit === 'TIER_100K') return 'Enterprise Scale (100,000 messages)'
      return 'Current Messaging Limit'
    },

    getMessagingLimitMessage (limit) {
      if (limit === 'TIER_250') {
        return 'You\'re currently at the starter limit of 250 messages. Verify your business or send quality messages to unlock higher limits.'
      }
      if (limit === 'TIER_2K') {
        return 'You\'ve unlocked 2,000 messages! Keep sending high-quality messages to automatically scale to higher tiers.'
      }
      if (limit === 'TIER_10K') {
        return 'Great progress! You\'re at 10,000 messages. Continue with quality messaging to reach the next tier.'
      }
      if (limit === 'TIER_100K') {
        return 'You\'re operating at enterprise scale with 100,000 messages. One more level to unlimited!'
      }
      return 'Check your current messaging limit and learn how to increase it.'
    }
  }
}
</script>

<style scoped>
.whatsapp-settings-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

.whatsapp-settings-container {
  max-width: 800px;
  margin: 0 auto;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 20px;
}

.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-spinner .spinner {
  animation: spin 1s linear infinite;
  color: #10b981;
}

.loading-container .loading-text {
  font-size: 16px;
  font-weight: 500;
  color: var(--text-secondary);
}

.dark .loading-spinner .spinner {
  color: #34d399;
}

.settings-header {
  margin-bottom: 32px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
}

.settings-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.settings-card {
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--border-primary);
  overflow: hidden;
}

.card-header {
  padding: 24px 24px 0;
}

.card-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px;
}

.card-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

.card-body {
  padding: 24px;
}

.settings-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 16px;
  width: 18px;
  height: 18px;
  opacity: 0.6;
  z-index: 2;
  color: var(--text-secondary);
}

.form-input {
  width: 100%;
  padding: 16px 16px 16px 52px;
  border: 2px solid var(--border-primary);
  border-radius: 12px;
  font-size: 16px;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.2s;
  outline: none;
}

.form-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input.error {
  border-color: #ef4444;
  background: #fef2f2;
}

.form-input.readonly-field {
  background: var(--bg-secondary);
  cursor: not-allowed;
  opacity: 0.7;
}

.warning-banner {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #fffbeb;
  border: 1px solid #fbbf24;
  border-radius: 12px;
  margin-bottom: 24px;
}

.warning-banner svg {
  flex-shrink: 0;
  color: #f59e0b;
}

.warning-banner div {
  flex: 1;
}

.warning-banner strong {
  display: block;
  color: #92400e;
  margin-bottom: 4px;
  font-size: 15px;
}

.warning-banner p {
  color: #78350f;
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
}

.toggle-password {
  position: absolute;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0.6;
  padding: 4px;
  border-radius: 4px;
  transition: opacity 0.2s;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-password:hover {
  opacity: 1;
}

.error-message {
  display: block;
  color: #ef4444;
  font-size: 14px;
  margin-top: 6px;
}

.update-button {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;
}

.update-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
}

.update-button:disabled {
  opacity: 0.8;
  cursor: not-allowed;
  transform: none;
}

.webhook-actions {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid;
}

.webhook-button {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border-color: var(--border-primary);
}

.webhook-button:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

.verify-button {
  background: var(--bg-secondary);
  color: #059669;
  border-color: #a7f3d0;
}

.verify-button:hover {
  background: var(--bg-tertiary);
  border-color: #6ee7b7;
}

.action-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.button-icon {
  width: 20px;
  height: 20px;
}

.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.success-message {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #059669;
  padding: 16px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
}

.error-message-card {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 16px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    margin-top: 64px;
  }

  .whatsapp-settings-container {
    padding: 16px;
  }

  .webhook-actions {
    flex-direction: column;
  }

  .action-button {
    justify-content: center;
  }

  .page-title {
    font-size: 24px;
  }

  .header-with-status {
    flex-direction: column;
    gap: 12px;
  }

  .connection-status {
    align-self: flex-start;
  }
}

/* Embedded Signup Styles */
.embedded-signup-card {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid #38bdf8;
}

.header-with-status {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.connection-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}

.connection-status.connected {
  background: #d1fae5;
  color: #065f46;
  border: 1px solid #10b981;
}

.connection-status.connected svg {
  color: #10b981;
}

.connection-status.disconnected {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #ef4444;
}

.connection-status.disconnected svg {
  color: #ef4444;
}

.embedded-signup-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.signup-info {
  display: flex;
  gap: 16px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 12px;
  border: 1px solid rgba(56, 189, 248, 0.3);
}

.info-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 12px;
  color: white;
}

.info-text {
  flex: 1;
  color: var(--text-primary);
}

.info-text p {
  margin: 0 0 12px 0;
  font-size: 15px;
}

.info-text ul {
  margin: 0;
  padding-left: 20px;
  list-style-type: disc;
}

.info-text li {
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-secondary);
}

/* Two-Button Layout Container */
.signup-buttons-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.signup-buttons-container button {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 48px 32px;
  min-height: 240px;
  background: linear-gradient(135deg, #25D366 0%, #1fa855 50%, #128C7E 100%);
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 10px 30px rgba(37, 211, 102, 0.3),
              0 4px 12px rgba(0, 0, 0, 0.1),
              inset 0 1px 0 rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.signup-buttons-container button::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 30% 40%, rgba(255, 255, 255, 0.15) 0%, transparent 60%);
  opacity: 1;
  transition: opacity 0.4s ease;
}

.signup-buttons-container button::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent 30%,
    rgba(255, 255, 255, 0.1) 50%,
    transparent 70%
  );
  opacity: 0;
  transform: rotate(45deg);
  transition: opacity 0.6s ease;
}

.signup-buttons-container button:hover::after {
  opacity: 1;
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    transform: rotate(45deg) translateX(-100%);
  }
  100% {
    transform: rotate(45deg) translateX(100%);
  }
}

.signup-buttons-container button:hover:not(:disabled) {
  transform: translateY(-6px) scale(1.02);
  box-shadow: 0 20px 50px rgba(37, 211, 102, 0.4),
              0 10px 20px rgba(0, 0, 0, 0.15),
              inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.signup-buttons-container button:active:not(:disabled) {
  transform: translateY(-3px) scale(1.01);
  transition: all 0.15s ease;
}

.signup-buttons-container button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.signup-buttons-container button .button-icon {
  width: 64px;
  height: 64px;
  fill: currentColor;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
}

.signup-buttons-container button:hover:not(:disabled) .button-icon {
  transform: scale(1.1) rotate(5deg);
}

.signup-buttons-container button span {
  position: relative;
  z-index: 1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  letter-spacing: 0.5px;
}

/* Specific button styles */
.embedded-signup-button {
  background: linear-gradient(135deg, #25D366 0%, #1fa855 50%, #128C7E 100%);
}

.coexistence-signup-button {
  background: linear-gradient(135deg, #128C7E 0%, #0d6d5f 50%, #075E54 100%);
}

.button-text-with-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.new-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  box-shadow: 0 4px 12px rgba(251, 191, 36, 0.4),
              0 0 0 3px rgba(255, 255, 255, 0.3);
  animation: pulse-badge 2s ease-in-out infinite;
  position: relative;
  overflow: hidden;
}

.new-badge::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: badge-shine 3s infinite;
}

.soon-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  color: white;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  box-shadow: 0 4px 15px rgba(107, 114, 128, 0.3),
              0 0 20px rgba(107, 114, 128, 0.2);
  position: relative;
  overflow: hidden;
}

@keyframes pulse-badge {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 4px 12px rgba(251, 191, 36, 0.4),
                0 0 0 3px rgba(255, 255, 255, 0.3);
  }
  50% {
    transform: scale(1.08);
    box-shadow: 0 6px 16px rgba(251, 191, 36, 0.6),
                0 0 0 4px rgba(255, 255, 255, 0.5);
  }
}

@keyframes badge-shine {
  0% {
    left: -100%;
  }
  100% {
    left: 200%;
  }
}

@media (max-width: 640px) {
  .signup-buttons-container {
    grid-template-columns: 1fr;
  }

  .signup-buttons-container button {
    min-height: 200px;
    padding: 36px 24px;
  }

  .signup-buttons-container button .button-icon {
    width: 56px;
    height: 56px;
  }
}

.or-divider {
  position: relative;
  text-align: center;
  margin: 8px 0;
}

.or-divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--border-primary);
}

.or-divider span {
  position: relative;
  display: inline-block;
  padding: 0 16px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 600;
}

.manual-setup-text {
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
  margin: 0;
  font-style: italic;
}

/* Dark mode adjustments for embedded signup */
@media (prefers-color-scheme: dark) {
  .embedded-signup-card {
    background: linear-gradient(135deg, #1e3a5f 0%, #2d5a8a 100%);
    border-color: #3b82f6;
  }

  .signup-info {
    background: rgba(30, 41, 59, 0.7);
    border-color: rgba(59, 130, 246, 0.3);
  }

  .or-divider span {
    background: linear-gradient(135deg, #1e3a5f 0%, #2d5a8a 100%);
  }
}

/* Connected Account Card */
.connected-account-card {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 2px solid #22c55e;
}

.account-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.info-item {
  background: rgba(255, 255, 255, 0.8);
  padding: 16px;
  border-radius: 12px;
  border: 1px solid rgba(34, 197, 94, 0.2);
}

.info-item-warning {
  border: 2px solid #f59e0b !important;
  background: rgba(251, 191, 36, 0.05);
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.15);
}

.info-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #15803d;
  margin-bottom: 8px;
}

.info-label svg {
  color: #22c55e;
}

.info-label svg.warning-icon-label {
  color: #f59e0b;
  filter: drop-shadow(0 0 4px rgba(245, 158, 11, 0.3));
}

.info-value {
  font-size: 14px;
  color: var(--text-primary);
  font-family: 'Courier New', monospace;
  word-break: break-all;
}

.badge-embedded {
  display: inline-block;
  padding: 4px 12px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.badge-webhook-active {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.badge-webhook-active::before {
  content: '';
  width: 8px;
  height: 8px;
  background: #86efac;
  border-radius: 50%;
  animation: pulse-dot 2s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.2);
  }
}

.account-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid rgba(34, 197, 94, 0.2);
}

.reconnect-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.reconnect-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.reconnect-button svg {
  width: 18px;
  height: 18px;
}

.disconnect-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #fee2e2;
  color: #dc2626;
  border: 2px solid #fecaca;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.disconnect-button:hover {
  background: #fecaca;
  border-color: #dc2626;
  transform: translateY(-1px);
}

.disconnect-button svg {
  width: 18px;
  height: 18px;
}

@media (max-width: 768px) {
  .account-info-grid {
    grid-template-columns: 1fr;
  }

  .account-actions {
    flex-direction: column;
  }

  .reconnect-button,
  .disconnect-button {
    width: 100%;
    justify-content: center;
  }
}

/* Webhook Secret Toggle Styles */
.info-value-with-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
}

.info-value.masked {
  flex: 1;
  min-width: 0;
}

.toggle-secret-button {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #15803d;
}

.toggle-secret-button:hover {
  background: rgba(34, 197, 94, 0.2);
  border-color: rgba(34, 197, 94, 0.5);
  transform: scale(1.05);
}

.toggle-secret-button svg {
  width: 16px;
  height: 16px;
}

/* Dark Mode Support */
.dark .embedded-signup-card {
  background: linear-gradient(135deg, #1e3a5f 0%, #2d5a8a 100%);
  border-color: #3b82f6;
}

.dark .signup-info {
  background: rgba(30, 41, 59, 0.7);
  border-color: rgba(59, 130, 246, 0.3);
}

.dark .or-divider span {
  background: linear-gradient(135deg, #1e3a5f 0%, #2d5a8a 100%);
}

.dark .connected-account-card {
  background: linear-gradient(135deg, #064e3b 0%, #065f46 100%);
  border-color: #10b981;
}

.dark .info-item {
  background: rgba(6, 78, 59, 0.3);
  border-color: rgba(16, 185, 129, 0.3);
}

.dark .info-item-warning {
  border-color: #fbbf24 !important;
  background: rgba(251, 191, 36, 0.1);
  box-shadow: 0 2px 8px rgba(251, 191, 36, 0.2);
}

.dark .info-label {
  color: #6ee7b7;
}

.dark .info-label svg {
  color: #10b981;
}

.dark .info-label svg.warning-icon-label {
  color: #fbbf24;
  filter: drop-shadow(0 0 4px rgba(251, 191, 36, 0.4));
}

.dark .toggle-secret-button {
  background: rgba(16, 185, 129, 0.15);
  border-color: rgba(16, 185, 129, 0.4);
  color: #6ee7b7;
}

.dark .toggle-secret-button:hover {
  background: rgba(16, 185, 129, 0.25);
  border-color: rgba(16, 185, 129, 0.6);
}

.dark .account-actions {
  border-top-color: rgba(16, 185, 129, 0.3);
}

.dark .reconnect-button {
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.4);
}

.dark .reconnect-button:hover {
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.5);
}

.dark .disconnect-button {
  background: rgba(239, 68, 68, 0.15);
  color: #fca5a5;
  border-color: rgba(239, 68, 68, 0.4);
}

.dark .disconnect-button:hover {
  background: rgba(239, 68, 68, 0.25);
  border-color: #ef4444;
  color: #fecaca;
}

.dark .warning-banner {
  background: rgba(251, 191, 36, 0.1);
  border-color: rgba(251, 191, 36, 0.3);
}

.dark .warning-banner svg {
  color: #fbbf24;
}

.dark .warning-banner strong {
  color: #fcd34d;
}

.dark .warning-banner p {
  color: #fde047;
}

.dark .form-input.error {
  background: rgba(239, 68, 68, 0.1);
  border-color: #ef4444;
  color: var(--text-primary);
}

.dark .connection-status.connected {
  background: rgba(16, 185, 129, 0.2);
  color: #6ee7b7;
  border-color: #10b981;
}

.dark .connection-status.disconnected {
  background: rgba(239, 68, 68, 0.2);
  color: #fca5a5;
  border-color: #ef4444;
}

.dark .success-message {
  background: rgba(16, 185, 129, 0.15);
  border-color: rgba(16, 185, 129, 0.4);
  color: #6ee7b7;
}

.dark .error-message-card {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.4);
  color: #fca5a5;
}

/* Recommendation Banner Styles */
.recommendation-banner {
  display: flex;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 2px solid #3b82f6;
  border-radius: 16px;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.banner-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 12px;
  color: white;
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.banner-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.banner-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e40af;
  margin: 0;
}

.banner-message {
  font-size: 14px;
  line-height: 1.6;
  color: #1e3a8a;
  margin: 0;
}

.banner-benefits {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
  margin-top: 8px;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #1e40af;
  font-weight: 500;
}

.benefit-item svg {
  flex-shrink: 0;
  color: #22c55e;
}

.banner-cta-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  align-self: flex-start;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.banner-cta-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
}

.banner-cta-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.banner-cta-button svg {
  width: 18px;
  height: 18px;
}

.loading-spinner-inline {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Dark mode for recommendation banner */
.dark .recommendation-banner {
  background: linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%);
  border-color: #3b82f6;
}

.dark .banner-title {
  color: #93c5fd;
}

.dark .banner-message {
  color: #bfdbfe;
}

.dark .benefit-item {
  color: #93c5fd;
}

.dark .banner-icon {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

@media (max-width: 768px) {
  .recommendation-banner {
    flex-direction: column;
    padding: 16px;
  }

  .banner-icon {
    width: 40px;
    height: 40px;
  }

  .banner-benefits {
    grid-template-columns: 1fr;
  }

  .banner-cta-button {
    width: 100%;
  }
}

/* Coexistence Mode Styles */
.coexistence-card {
  background: linear-gradient(135deg, #fefce8 0%, #fef9c3 100%);
  border: 2px solid #eab308;
}

.coexistence-info {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.coexistence-icon {
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
}

.coexistence-description {
  font-size: 15px;
  line-height: 1.6;
  color: var(--text-primary);
  margin: 0;
}

.coexistence-benefits h4,
.coexistence-setup h4 {
  font-size: 16px;
  font-weight: 600;
  color: #854d0e;
  margin: 0 0 12px 0;
}

.coexistence-benefits ul {
  margin: 0;
  padding-left: 24px;
  list-style-type: disc;
}

.coexistence-benefits li {
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: #78350f;
}

.coexistence-setup ol {
  margin: 0;
  padding-left: 24px;
  list-style-type: decimal;
}

.coexistence-setup li {
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: #78350f;
}

.pin-input-container {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-top: 16px;
}

.pin-input {
  font-size: 24px;
  letter-spacing: 8px;
  text-align: center;
  width: 200px;
  padding: 12px;
  border: 2px solid #eab308;
  border-radius: 8px;
  font-family: monospace;
  background: white;
  color: #000;
  outline: none;
  transition: all 0.2s;
}

.pin-input:focus {
  border-color: #ca8a04;
  box-shadow: 0 0 0 3px rgba(234, 179, 8, 0.1);
}

.enable-btn {
  padding: 12px 24px;
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(37, 211, 102, 0.3);
}

.enable-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.4);
}

.enable-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.coexistence-enabled {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 20px;
  gap: 16px;
}

.coexistence-enabled svg {
  color: #22c55e;
}

.coexistence-enabled h4 {
  font-size: 20px;
  font-weight: 700;
  color: #15803d;
  margin: 0;
}

.coexistence-enabled p {
  font-size: 15px;
  line-height: 1.6;
  color: #166534;
  margin: 0;
  max-width: 500px;
}

.dark .coexistence-card {
  background: linear-gradient(135deg, #422006 0%, #713f12 100%);
  border-color: #ca8a04;
}

.dark .coexistence-benefits h4,
.dark .coexistence-setup h4 {
  color: #fef08a;
}

.dark .coexistence-benefits li,
.dark .coexistence-setup li {
  color: #fde047;
}

.dark .pin-input {
  background: rgba(254, 252, 232, 0.1);
  color: #fef08a;
  border-color: #ca8a04;
}

.dark .coexistence-enabled h4 {
  color: #86efac;
}

.dark .coexistence-enabled p {
  color: #6ee7b7;
}

/* Phone Status Badges */
.badge-phone-status {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-phone-status.status-connected {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
}

.badge-phone-status.status-pending {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(251, 191, 36, 0.3);
}

.badge-phone-status.status-unknown {
  background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(148, 163, 184, 0.3);
}

/* Quality Rating Badges */
.badge-quality-rating {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-quality-rating.quality-green {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
}

.badge-quality-rating.quality-yellow {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(251, 191, 36, 0.3);
}

.badge-quality-rating.quality-red {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

.badge-quality-rating.quality-unknown {
  background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(148, 163, 184, 0.3);
}

/* Phone Status Refresh Section */
.phone-status-refresh {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 20px;
  margin-top: 20px;
  border-top: 1px solid rgba(34, 197, 94, 0.2);
}

.refresh-status-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
}

.refresh-status-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(139, 92, 246, 0.4);
}

.refresh-status-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.refresh-status-button svg {
  transition: transform 0.6s ease;
}

.refresh-status-button svg.spinning {
  animation: spin-refresh 1s linear infinite;
}

@keyframes spin-refresh {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.last-check-time {
  font-size: 12px;
  color: var(--text-secondary);
  text-align: center;
  font-style: italic;
}

/* Dark mode for phone status */
.dark .phone-status-refresh {
  border-top-color: rgba(16, 185, 129, 0.3);
}

.dark .refresh-status-button {
  background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.4);
}

.dark .refresh-status-button:hover:not(:disabled) {
  box-shadow: 0 6px 16px rgba(124, 58, 237, 0.5);
}

/* Account Review Status Badges */
.badge-review-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.badge-review-status.review-approved {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.badge-review-status.review-pending {
  background: rgba(251, 191, 36, 0.1);
  color: #f59e0b;
  border: 1px solid rgba(251, 191, 36, 0.3);
}

.badge-review-status.review-rejected {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.badge-review-status.review-unknown,
.badge-review-status.review-error {
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
  border: 1px solid rgba(107, 114, 128, 0.3);
}

/* Meta Review Badge */
.badge-meta-review {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 600;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(37, 99, 235, 0.12) 100%);
  color: #3b82f6;
  border: 1.5px solid rgba(59, 130, 246, 0.4);
}

.dark .badge-meta-review {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.18) 0%, rgba(37, 99, 235, 0.18) 100%);
  color: #60a5fa;
  border-color: rgba(59, 130, 246, 0.5);
}

/* Business Verification Status Badges */
.badge-verification-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.badge-verification-status.verification-approved,
.badge-verification-status.verification-verified {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.badge-verification-status.verification-pending,
.badge-verification-status.verification-not_verified {
  background: rgba(251, 191, 36, 0.1);
  color: #f59e0b;
  border: 1px solid rgba(251, 191, 36, 0.3);
}

.badge-verification-status.verification-rejected {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.badge-verification-status.verification-unknown,
.badge-verification-status.verification-error {
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
  border: 1px solid rgba(107, 114, 128, 0.3);
}

/* Loading Badge */
.badge-loading {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.spinner-small {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(59, 130, 246, 0.3);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Quali-D WhatsApp Connect Badge */
.badge-qualid-active {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.dark .badge-qualid-active {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.4);
}

/* OTP Verification Card */
.otp-verification-card {
  margin-bottom: 24px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.05) 100%);
  border: 3px solid rgba(16, 185, 129, 0.4);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.15);
  animation: pulse-border 2s ease-in-out infinite;
}

@keyframes pulse-border {
  0%, 100% {
    border-color: rgba(16, 185, 129, 0.4);
    box-shadow: 0 8px 24px rgba(16, 185, 129, 0.15);
  }
  50% {
    border-color: rgba(16, 185, 129, 0.6);
    box-shadow: 0 8px 32px rgba(16, 185, 129, 0.25);
  }
}

.otp-card-header {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 32px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%);
  border-bottom: 2px solid rgba(16, 185, 129, 0.2);
}

.otp-icon-container {
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 20px;
  color: white;
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.otp-header-content {
  flex: 1;
}

.otp-title {
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.2;
}

.otp-subtitle {
  margin: 0;
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.5;
}

.otp-card-body {
  padding: 32px;
}

.otp-input-section {
  margin-bottom: 24px;
}

.otp-label {
  display: block;
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}

.otp-input-container {
  position: relative;
}

.otp-input {
  width: 100%;
  padding: 20px 80px 20px 24px;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: 12px;
  text-align: center;
  border: 3px solid var(--border-primary);
  border-radius: 16px;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.3s ease;
  font-family: 'Courier New', monospace;
}

.otp-input:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1);
}

.otp-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.otp-counter {
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
}

.otp-hint {
  margin: 12px 0 0 0;
  font-size: 13px;
  color: var(--text-secondary);
  font-style: italic;
}

.otp-actions {
  display: flex;
  justify-content: center;
}

.otp-submit-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 48px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.3);
}

.otp-submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(16, 185, 129, 0.4);
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.otp-submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.otp-submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.otp-error {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
  padding: 16px;
  background: rgba(239, 68, 68, 0.1);
  border: 2px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  color: #dc2626;
  font-size: 14px;
  font-weight: 600;
}

.otp-success {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
  padding: 16px;
  background: rgba(16, 185, 129, 0.1);
  border: 2px solid rgba(16, 185, 129, 0.3);
  border-radius: 12px;
  color: #059669;
  font-size: 14px;
  font-weight: 600;
}

.dark .otp-verification-card {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.08) 100%);
  border-color: rgba(16, 185, 129, 0.5);
}

.dark .otp-card-header {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%);
}

/* Meta Review Card */
.meta-review-card {
  margin-top: 20px;
  padding: 0;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.05) 100%);
  border: 2px solid rgba(59, 130, 246, 0.3);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
}

.meta-review-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%);
  border-bottom: 1px solid rgba(59, 130, 246, 0.2);
}

.meta-review-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 50%;
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.meta-review-title-section {
  flex: 1;
}

.meta-review-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #3b82f6;
}

.meta-review-body {
  padding: 20px;
}

.meta-review-section {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.meta-review-section:last-of-type {
  margin-bottom: 24px;
}

.meta-review-section-icon {
  flex-shrink: 0;
  font-size: 24px;
  line-height: 1;
}

.meta-review-section-content {
  flex: 1;
}

.meta-review-section-title {
  margin: 0 0 8px 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}

.meta-review-text {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
  font-weight: 500;
}

.meta-review-list {
  margin: 0;
  padding-left: 20px;
  list-style-type: disc;
}

.meta-review-list li {
  margin-bottom: 6px;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
}

.meta-review-list li:last-child {
  margin-bottom: 0;
}

/* What To Do Now Button */
.meta-review-action {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(59, 130, 246, 0.2);
  display: flex;
  justify-content: center;
}

.what-to-do-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.what-to-do-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
  background: linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%);
}

.what-to-do-btn svg {
  flex-shrink: 0;
}

.dark .meta-review-card {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(37, 99, 235, 0.08) 100%);
  border-color: rgba(59, 130, 246, 0.4);
}

.dark .meta-review-header {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%);
  border-bottom-color: rgba(59, 130, 246, 0.3);
}

.dark .meta-review-title {
  color: #60a5fa;
}

/* Account Warning */
.account-warning {
  margin-top: 20px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(220, 38, 38, 0.05) 100%);
  border: 2px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.warning-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 50%;
  color: #ef4444;
}

.warning-content {
  flex: 1;
}

.warning-title {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 700;
  color: #ef4444;
}

.warning-message {
  margin: 0 0 12px 0;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.5;
}

.warning-action-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #ef4444;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
}

.warning-action-link:hover {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.warning-action-link svg {
  width: 14px;
  height: 14px;
}

/* Verification Info */
.verification-info {
  margin-top: 20px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.05) 100%);
  border: 2px solid rgba(59, 130, 246, 0.3);
  border-radius: 12px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.info-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.1);
  border-radius: 50%;
  color: #3b82f6;
}

.info-content {
  flex: 1;
}

.info-title {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 700;
  color: #3b82f6;
}

.info-message {
  margin: 0 0 12px 0;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.5;
}

.info-action-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #3b82f6;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
}

.info-action-link:hover {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.info-action-link svg {
  width: 14px;
  height: 14px;
}

/* Messaging Limit Badge */
.badge-messaging-limit {
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.25);
}

.dark .badge-messaging-limit {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  box-shadow: 0 2px 6px rgba(5, 150, 105, 0.3);
}

/* Messaging Limit Info Box */
.messaging-limit-info {
  margin-top: 20px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.05) 100%);
  border: 2px solid rgba(59, 130, 246, 0.3);
  border-radius: 12px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.limit-info-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.15);
  border-radius: 8px;
  color: #3b82f6;
}

.limit-info-content {
  flex: 1;
}

.limit-info-title {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
}

.limit-info-message {
  margin: 0 0 16px 0;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.5;
}

.scaling-paths {
  margin: 16px 0;
  padding: 12px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 8px;
}

.scaling-paths h5 {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.scaling-paths ul {
  margin: 8px 0;
  padding-left: 20px;
}

.scaling-paths li {
  margin: 6px 0;
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.5;
}

.scaling-paths li strong {
  color: var(--text-primary);
  font-weight: 600;
}

.limit-action-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #3b82f6;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
}

.limit-action-link:hover {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.limit-action-link svg {
  width: 14px;
  height: 14px;
}
</style>
